import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Star,
  Search,
  Filter,
  MessageSquare,
  Calendar,
  MapPin,
  TrendingUp,
  Users,
  Clock,
  AlertCircle,
  RefreshCw,
  Globe,
  Database,
  Check,
  Eye,
  Edit,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  CalendarIcon
} from 'lucide-react'
import { format } from 'date-fns'
import { useReviews, useReviewStats } from '@/hooks/useReviews'
import { useReviewsFilters } from '@/hooks/useReviewsFilters'
import EnhancedReviewStats from '@/features/reviews/components/EnhancedReviewStats'
import { SyncStatus } from '@/features/gmb/components/shared/SyncStatus'
import { SyncStatusIndicator } from '@/features/gmb/components/shared/SyncStatusWidget'
import { syncReviews } from '@/utils/gmbSync'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { GmbSyncType } from '@/types/gmb'
import type { ReviewSortField } from '@/types/reviews'
import ReviewResponse from '@/features/gmb/components/reviews/ReviewResponse'

type ReviewSource = 'manual' | 'gmb' | 'jobber' | 'website' | 'other'

export default function ReviewsManagement() {
  const filtersState = useReviewsFilters()
  const { filters, sort, page, pageSize } = filtersState
  const [sourceFilter, setSourceFilter] = useState<ReviewSource | 'all'>('all')
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [isResponseSheetOpen, setIsResponseSheetOpen] = useState(false)
  const [selectedReview, setSelectedReview] = useState<any>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [dateFrom, setDateFrom] = useState<Date | undefined>()
  const [dateTo, setDateTo] = useState<Date | undefined>()
  const queryClient = useQueryClient()

  // Handle sort toggle for table headers
  const handleSort = (field: ReviewSortField) => {
    if (sort.field === field) {
      // Toggle direction if same field
      filtersState.setSort({
        field,
        direction: sort.direction === 'asc' ? 'desc' : 'asc'
      })
    } else {
      // Default to descending for new field
      filtersState.setSort({ field, direction: 'desc' })
    }
  }

  // Get sort icon for a field
  const getSortIcon = (field: string) => {
    if (sort.field !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-50" />
    }
    return sort.direction === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1" />
  }

  // Apply date filters
  const applyDateFilter = () => {
    filtersState.setDateRange(
      dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
      dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined
    )
  }

  // Clear date filters
  const clearDateFilter = () => {
    setDateFrom(undefined)
    setDateTo(undefined)
    filtersState.setDateRange(undefined, undefined)
  }

  // Fetch reviews data with source filter
  const reviewsFilters = {
    ...filters,
    ...(sourceFilter !== 'all' && { source: sourceFilter }),
    // Default to GMB source when in GMB integration page
    source: sourceFilter === 'all' ? 'gmb' : sourceFilter
  }

  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    error: reviewsError
  } = useReviews({ filters: reviewsFilters, sort, page, page_size: pageSize })

  // Fetch stats data - filter by 'gmb' source for GMB-specific stats
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError
  } = useReviewStats('gmb')

  // Fetch GMB sync logs
  const { data: syncLogs = [] } = useQuery({
    queryKey: ['gmb-sync-logs', 'reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_sync_log')
        .select('*')
        .eq('sync_type', 'reviews')
        .order('started_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data || []
    }
  })

  // Fetch GMB locations
  const { data: gmbLocations = [] } = useQuery({
    queryKey: ['gmb-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_locations')
        .select('*')
        .eq('is_active', true)
        .order('location_name')

      if (error) throw error
      return data || []
    }
  })

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async (locationIds?: string[]) => {
      return await syncReviews(locationIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      queryClient.invalidateQueries({ queryKey: ['review-stats'] })
      queryClient.invalidateQueries({ queryKey: ['gmb-sync-logs'] })
    }
  })

  // Handle opening response sheet
  const handleOpenResponse = (review: any) => {
    setSelectedReview(review)
    setIsResponseSheetOpen(true)
  }

  // Handle closing response sheet
  const handleCloseResponse = () => {
    setSelectedReview(null)
    setIsResponseSheetOpen(false)
  }

  // Handle successful response save/publish
  const handleResponseSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['reviews'] })
    queryClient.invalidateQueries({ queryKey: ['review-stats'] })
  }

  if (reviewsError || statsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
            <h3 className="text-lg font-semibold">Error Loading Reviews</h3>
            <p className="text-muted-foreground">
              {reviewsError?.message || statsError?.message || 'Unable to load reviews data'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reviews Management</h1>
          <p className="text-muted-foreground">
            Monitor and respond to reviews from all sources
          </p>
          <div className="mt-2">
            <SyncStatusIndicator syncType={GmbSyncType.REVIEWS} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.href = '/integrations/gmb/sync-reviews'}
            className="flex-1 sm:flex-none"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            <span className="truncate">GMB Sync Settings</span>
          </Button>
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
            <MessageSquare className="w-4 h-4 mr-2" />
            <span className="truncate">Templates</span>
          </Button>
          <Button size="sm" className="flex-1 sm:flex-none">
            <TrendingUp className="w-4 h-4 mr-2" />
            <span className="truncate">Analytics</span>
          </Button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="enhanced">Enhanced Analytics</TabsTrigger>
          <TabsTrigger value="sync">GMB Sync Status</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Basic Stats Overview */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.total_reviews?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground">
                  <span className={`${(stats?.reviews_this_month || 0) >= 0 ? 'text-chart-5' : 'text-destructive'}`}>
                    {(stats?.reviews_this_month || 0) >= 0 ? '+' : ''}{stats?.reviews_this_month || 0}
                  </span> this month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold flex items-center gap-1">
                  {statsLoading ? '...' : stats?.average_rating || '0'}
                  {!statsLoading && stats?.average_rating && (
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= Math.floor(stats.average_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  <span className={`${(stats?.rating_change || 0) >= 0 ? 'text-chart-5' : 'text-destructive'}`}>
                    {(stats?.rating_change || 0) >= 0 ? '+' : ''}{stats?.rating_change || 0}
                  </span> from last month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : `${stats?.response_rate || 0}%`}
                </div>
                <p className="text-xs text-muted-foreground">
                  <span className={`${(stats?.response_rate_change || 0) >= 0 ? 'text-chart-5' : 'text-destructive'}`}>
                    {(stats?.response_rate_change || 0) >= 0 ? '+' : ''}{stats?.response_rate_change || 0}%
                  </span> this month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Responses</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? '...' : stats?.pending_responses || '0'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats?.pending_responses && stats.pending_responses > 5 && (
                    <span className="text-accent">Needs attention</span>
                  )}
                  {stats?.pending_responses && stats.pending_responses <= 5 && (
                    <span className="text-chart-5">Under control</span>
                  )}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="enhanced" className="space-y-6">
          <EnhancedReviewStats />
        </TabsContent>

        <TabsContent value="sync" className="space-y-6">
          <SyncStatus
            syncLogs={syncLogs}
            isSyncing={syncMutation.isPending}
            onSync={async (syncType) => {
              if (syncType === 'reviews') {
                await syncMutation.mutateAsync(selectedLocations.length > 0 ? selectedLocations : undefined)
              }
              // Handle other sync types if needed
            }}
            showDetailedLogs={true}
          />
        </TabsContent>
      </Tabs>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            <div>
              <CardTitle>Reviews</CardTitle>
              <CardDescription>
                All reviews from Google My Business and manual entries
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search reviews..."
                  className="pl-10 w-full"
                  value={filters.search || ''}
                  onChange={(e) => filtersState.setSearch(e.target.value)}
                />
              </div>
              <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as ReviewSource | 'all')}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="gmb">Google My Business</SelectItem>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                  <SelectItem value="jobber">Jobber</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                </SelectContent>
              </Select>

              {/* Filters Popover */}
              <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {filtersState.filterCount > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                        {filtersState.filterCount}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">Filter Reviews</h4>
                      {filtersState.hasActiveFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            filtersState.clearFilters()
                            clearDateFilter()
                            setIsFilterOpen(false)
                          }}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>

                    <Separator />

                    {/* Rating Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Rating</Label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Button
                            key={rating}
                            variant={filters.rating_min === rating ? "default" : "outline"}
                            size="sm"
                            className="flex-1 h-8 px-2"
                            onClick={() => {
                              if (filters.rating_min === rating) {
                                filtersState.setRatingRange(undefined, undefined)
                              } else {
                                filtersState.setRatingRange(rating, rating)
                              }
                            }}
                          >
                            <Star className={`w-3 h-3 mr-0.5 ${filters.rating_min === rating ? 'fill-current' : ''}`} />
                            {rating}
                          </Button>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant={filters.rating_min === 4 && filters.rating_max === 5 ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            if (filters.rating_min === 4 && filters.rating_max === 5) {
                              filtersState.setRatingRange(undefined, undefined)
                            } else {
                              filtersState.setRatingRange(4, 5)
                            }
                          }}
                        >
                          Positive (4-5)
                        </Button>
                        <Button
                          variant={filters.rating_min === 1 && filters.rating_max === 3 ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            if (filters.rating_min === 1 && filters.rating_max === 3) {
                              filtersState.setRatingRange(undefined, undefined)
                            } else {
                              filtersState.setRatingRange(1, 3)
                            }
                          }}
                        >
                          Needs Attention (1-3)
                        </Button>
                      </div>
                    </div>

                    <Separator />

                    {/* Response Status Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Response Status</Label>
                      <div className="flex gap-2">
                        <Button
                          variant={filters.response_needed === true ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            if (filters.response_needed === true) {
                              filtersState.setResponseFilter(undefined, undefined)
                            } else {
                              filtersState.setResponseFilter(undefined, true)
                            }
                          }}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Needs Response
                        </Button>
                        <Button
                          variant={filters.has_response === true ? "default" : "outline"}
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            if (filters.has_response === true) {
                              filtersState.setResponseFilter(undefined, undefined)
                            } else {
                              filtersState.setResponseFilter(true, undefined)
                            }
                          }}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Responded
                        </Button>
                      </div>
                    </div>

                    <Separator />

                    {/* Date Range Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Date Range</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="justify-start text-xs font-normal">
                              <CalendarIcon className="w-3 h-3 mr-1" />
                              {dateFrom ? format(dateFrom, 'MMM d, yy') : 'From'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={dateFrom}
                              onSelect={(date) => {
                                setDateFrom(date)
                                if (date) applyDateFilter()
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="justify-start text-xs font-normal">
                              <CalendarIcon className="w-3 h-3 mr-1" />
                              {dateTo ? format(dateTo, 'MMM d, yy') : 'To'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={dateTo}
                              onSelect={(date) => {
                                setDateTo(date)
                                if (date) applyDateFilter()
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      {(dateFrom || dateTo) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs h-7"
                          onClick={clearDateFilter}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Clear Dates
                        </Button>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            const today = new Date()
                            const weekAgo = new Date(today)
                            weekAgo.setDate(today.getDate() - 7)
                            setDateFrom(weekAgo)
                            setDateTo(today)
                            filtersState.setDateRange(
                              format(weekAgo, 'yyyy-MM-dd'),
                              format(today, 'yyyy-MM-dd')
                            )
                          }}
                        >
                          Last 7 Days
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            const today = new Date()
                            const monthAgo = new Date(today)
                            monthAgo.setDate(today.getDate() - 30)
                            setDateFrom(monthAgo)
                            setDateTo(today)
                            filtersState.setDateRange(
                              format(monthAgo, 'yyyy-MM-dd'),
                              format(today, 'yyyy-MM-dd')
                            )
                          }}
                        >
                          Last 30 Days
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Quick Filter Chips & Active Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Quick Filters - always visible */}
            <span className="text-xs text-muted-foreground mr-1">Quick:</span>
            <Badge
              variant={filters.response_needed === true ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/10"
              onClick={() => {
                if (filters.response_needed === true) {
                  filtersState.setResponseFilter(undefined, undefined)
                } else {
                  filtersState.setResponseFilter(undefined, true)
                }
              }}
            >
              <Clock className="w-3 h-3 mr-1" />
              Needs Response
            </Badge>
            <Badge
              variant={filters.rating_min === 1 && filters.rating_max === 3 ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/10"
              onClick={() => {
                if (filters.rating_min === 1 && filters.rating_max === 3) {
                  filtersState.setRatingRange(undefined, undefined)
                } else {
                  filtersState.setRatingRange(1, 3)
                }
              }}
            >
              <Star className="w-3 h-3 mr-1" />
              Low Ratings (1-3)
            </Badge>
            <Badge
              variant={filters.date_from && new Date(filters.date_from) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/10"
              onClick={() => {
                const today = new Date()
                const weekAgo = new Date(today)
                weekAgo.setDate(today.getDate() - 7)
                if (filters.date_from && new Date(filters.date_from) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
                  setDateFrom(undefined)
                  setDateTo(undefined)
                  filtersState.setDateRange(undefined, undefined)
                } else {
                  setDateFrom(weekAgo)
                  setDateTo(today)
                  filtersState.setDateRange(format(weekAgo, 'yyyy-MM-dd'), format(today, 'yyyy-MM-dd'))
                }
              }}
            >
              <Calendar className="w-3 h-3 mr-1" />
              Last 7 Days
            </Badge>

            {/* Separator */}
            {filtersState.hasActiveFilters && (
              <Separator orientation="vertical" className="h-4 mx-1" />
            )}

            {/* Active Filter Display */}
            {filters.search && (
              <Badge variant="secondary" className="gap-1">
                Search: "{filters.search}"
                <X
                  className="w-3 h-3 cursor-pointer hover:text-destructive"
                  onClick={() => filtersState.setSearch('')}
                />
              </Badge>
            )}
            {(filters.rating_min !== undefined && filters.rating_max !== undefined &&
              !(filters.rating_min === 1 && filters.rating_max === 3)) && (
              <Badge variant="secondary" className="gap-1">
                Rating: {filters.rating_min === filters.rating_max
                  ? `${filters.rating_min} star${filters.rating_min !== 1 ? 's' : ''}`
                  : `${filters.rating_min}-${filters.rating_max} stars`
                }
                <X
                  className="w-3 h-3 cursor-pointer hover:text-destructive"
                  onClick={() => filtersState.setRatingRange(undefined, undefined)}
                />
              </Badge>
            )}
            {(filters.date_from || filters.date_to) &&
              !(filters.date_from && new Date(filters.date_from) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) && (
              <Badge variant="secondary" className="gap-1">
                Date: {filters.date_from && format(new Date(filters.date_from), 'MMM d')}
                {filters.date_from && filters.date_to && ' - '}
                {filters.date_to && format(new Date(filters.date_to), 'MMM d')}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-destructive"
                  onClick={clearDateFilter}
                />
              </Badge>
            )}
            {filters.has_response === true && (
              <Badge variant="secondary" className="gap-1">
                Has Response
                <X
                  className="w-3 h-3 cursor-pointer hover:text-destructive"
                  onClick={() => filtersState.setResponseFilter(undefined, undefined)}
                />
              </Badge>
            )}
            {filtersState.filterCount > 2 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6 px-2"
                onClick={() => {
                  filtersState.clearFilters()
                  clearDateFilter()
                }}
              >
                Clear All ({filtersState.filterCount})
              </Button>
            )}
          </div>

          {/* Reviews Table */}
          <div className="border rounded-lg">
            {/* Sortable Table Header */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 p-4 border-b bg-muted/50 text-sm font-medium">
              <div className="md:col-span-2">Reviewer</div>
              <div>Location</div>
              <button
                className="flex items-center text-left hover:text-primary transition-colors"
                onClick={() => handleSort('star_rating')}
              >
                Rating
                {getSortIcon('star_rating')}
              </button>
              <button
                className="flex items-center text-left hover:text-primary transition-colors"
                onClick={() => handleSort('reviewer_create_time')}
              >
                Date
                {getSortIcon('reviewer_create_time')}
              </button>
              <div>Actions</div>
            </div>

            {/* Loading State */}
            {reviewsLoading && (
              <div className="p-8 text-center">
                <div className="text-muted-foreground">Loading reviews...</div>
              </div>
            )}

            {/* No Results */}
            {!reviewsLoading && reviewsData && reviewsData.reviews.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-muted-foreground">
                  {filtersState.hasActiveFilters ? 'No reviews match your filters' : 'No reviews found'}
                </div>
                {filtersState.hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={filtersState.clearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}

            {/* Review Rows */}
            {reviewsData?.reviews.map((review) => {
              const reviewSource = review.source || (review.gmb_review_id ? 'gmb' : 'manual')
              const sourceIcon = reviewSource === 'gmb' ? <Globe className="w-3 h-3" /> : <Database className="w-3 h-3" />
              const sourceColor = reviewSource === 'gmb' ? 'bg-chart-4/10 text-chart-4' : 'bg-muted text-muted-foreground'

              // Determine response status from review data
              const hasResponse = !!review.reply_comment
              const responseStatus = review.response_status || (hasResponse ? 'published' : null)

              return (
                <div key={review.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                  {/* Desktop View */}
                  <div className="hidden md:grid md:grid-cols-6 gap-4 p-4 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{review.reviewer_name}</div>
                          <div className="text-xs text-muted-foreground truncate" title={review.reviewer_comment}>
                            {review.reviewer_comment}
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <Badge variant="secondary" className={`text-[10px] h-4 px-1.5 ${sourceColor}`}>
                              {sourceIcon}
                              <span className="ml-1">{reviewSource.toUpperCase()}</span>
                            </Badge>
                            {review.services && (
                              <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                                {review.services.name}
                              </Badge>
                            )}
                            {responseStatus && (
                              <Badge
                                variant={responseStatus === 'draft' ? 'secondary' : 'default'}
                                className={`text-[10px] h-4 px-1.5 ${responseStatus === 'published' ? 'bg-chart-5/10 text-chart-5 hover:bg-chart-5/20 border-transparent' : ''}`}
                              >
                                {responseStatus === 'published' && <Check className="w-2.5 h-2.5 mr-1" />}
                                {responseStatus.charAt(0).toUpperCase() + responseStatus.slice(1)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center space-x-1.5 text-sm text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="truncate">{review.location_name}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex space-x-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-3.5 h-3.5 ${star <= (review.star_rating_number || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(review.reviewer_create_time).toLocaleDateString()}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleOpenResponse(review)}>
                        <Eye className="w-4 h-4" />
                        <span className="sr-only">View</span>
                      </Button>
                      <Button size="sm" className="h-8 px-3" onClick={() => handleOpenResponse(review)}>
                        {hasResponse ? (responseStatus === 'published' ? 'Update' : 'Edit') : 'Respond'}
                      </Button>
                    </div>
                  </div>

                  {/* Mobile View */}
                  <div className="md:hidden p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                          <Users className="w-4.5 h-4.5 text-primary" />
                        </div>
                        <div>
                          <div className="font-semibold text-sm">{review.reviewer_name}</div>
                          <div className="flex space-x-0.5 mt-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${star <= (review.star_rating_number || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {new Date(review.reviewer_create_time).toLocaleDateString()}
                      </span>
                    </div>

                    {review.reviewer_comment && (
                      <p className="text-sm text-foreground/80 leading-relaxed line-clamp-2 italic italic-muted px-1">
                        "{review.reviewer_comment}"
                      </p>
                    )}

                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="secondary" className={`text-[10px] h-4.5 px-1.5 ${sourceColor}`}>
                        {sourceIcon}
                        <span className="ml-1">{reviewSource.toUpperCase()}</span>
                      </Badge>
                      <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-muted/50 text-muted-foreground text-[10px]">
                        <MapPin className="w-3 h-3" />
                        <span>{review.location_name}</span>
                      </div>
                      {responseStatus && (
                        <Badge
                          variant={responseStatus === 'draft' ? 'secondary' : 'default'}
                          className={`text-[10px] h-4.5 px-1.5 ${responseStatus === 'published' ? 'bg-chart-5/10 text-chart-5 hover:bg-chart-5/20' : ''}`}
                        >
                          {responseStatus === 'published' ? <Check className="w-2.5 h-2.5 mr-1" /> : <Clock className="w-2.5 h-2.5 mr-1" />}
                          {responseStatus.charAt(0).toUpperCase() + responseStatus.slice(1)}
                        </Badge>
                      )}
                    </div>

                    <div className="flex gap-2 pt-1">
                      <Button variant="outline" size="sm" className="flex-1 h-9 gap-1.5" onClick={() => handleOpenResponse(review)}>
                        <Eye className="w-3.5 h-3.5" />
                        View Info
                      </Button>
                      <Button size="sm" className="flex-1 h-9 gap-1.5" onClick={() => handleOpenResponse(review)}>
                        {hasResponse ? <Edit className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
                        {hasResponse ? 'Edit Response' : 'Respond'}
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {reviewsData && reviewsData.reviews.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, reviewsData.total_count)} of {reviewsData.total_count.toLocaleString()} reviews
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => filtersState.setPage(page - 1)}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, reviewsData.total_pages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(
                    reviewsData.total_pages - 4,
                    Math.max(1, page - 2)
                  )) + i

                  return pageNum <= reviewsData.total_pages ? (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => filtersState.setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ) : null
                })}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= reviewsData.total_pages}
                  onClick={() => filtersState.setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Response Sheet */}
      <Sheet open={isResponseSheetOpen} onOpenChange={setIsResponseSheetOpen}>
        <SheetContent className="w-full sm:w-[600px] sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Review Response</SheetTitle>
            <SheetDescription>
              Manage your response to this customer review
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {selectedReview && (
              <ReviewResponse
                review={selectedReview}
                onClose={handleCloseResponse}
                onSuccess={handleResponseSuccess}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
